import React, { useCallback, useMemo } from 'react'
import { Loading, Message } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useWorkAuthorizationExpirationReport } from 'queries/admin/i9/useWorkAuthorizationExpirationReport'
import { useI9AltDocsHubQueryState } from '../hooks/useI9AltDocsHubQueryState'
import { useI9ReverificationHubQueryState } from '../hooks/useI9ReverificationHubQueryState'
import {
  filterAltDocRows,
  filterReverificationRows,
  groupAltDocRows,
  groupReverificationRows,
  type AltDocHubRow,
  type AltDocsDateSort,
  type AltDocsGroupBy,
  type ReverificationExpirationFilterDays,
  type ReverificationGroupBy,
} from './i9HubRows'
import {
  buildMarketSelectOptions,
  buildStateSelectOptions,
  useClearStaleQueryParam,
} from './i9HubSelectOptions'
import { EXPIRATION_OPTIONS } from '../components/I9HubFilters/ExpirationWindowUrlFilter'

type WorkAuthorizationExpirationRow = NonNullable<
  ReturnType<typeof useWorkAuthorizationExpirationReport>['data']
>[number]

type I9HubContextState = {
  state: {
    altDocs: {
      items: AltDocHubRow[]
      filteredItems: AltDocHubRow[]
      groupedItems: [string, AltDocHubRow[]][]
      marketSelectOptions: { label: string; value: string }[]
      stateSelectOptions: { label: string; value: string }[]
      groupBy: AltDocsGroupBy
      dateSort: AltDocsDateSort
      filters: {
        state: string | null
        market: string | null
        workerId: number | null
        workerSearch: string
      }
    }
    reverification: {
      reportRows: WorkAuthorizationExpirationRow[]
      filteredRows: WorkAuthorizationExpirationRow[]
      groupedRows: [string, WorkAuthorizationExpirationRow[]][]
      marketSelectOptions: { label: string; value: string }[]
      stateSelectOptions: { label: string; value: string }[]
      groupBy: ReverificationGroupBy
      expirationFilterDays: ReverificationExpirationFilterDays
      filters: {
        state: string | null
        market: string | null
        workerId: number | null
        workerSearch: string
      }
      isLoading: boolean
      isError: boolean
      errorMessage: string
      isFetching: boolean
      dataUpdatedAt: number
    }
    counts: {
      alternateDocuments: number
      i9Reverification: number
    }
  }
  actions: {
    setAltDocsQuery: ReturnType<typeof useI9AltDocsHubQueryState>[1]
    setReverificationQuery: ReturnType<
      typeof useI9ReverificationHubQueryState
    >[1]
    refetchReverificationReport: () => void
  }
}

const [Context, useI9HubContextValue] = createContext<I9HubContextState>({
  name: 'I9HubContext',
})

type I9HubProviderProps = {
  children: React.ReactNode
  /** Alternate-doc rows when the queue API exists */
  altDocsItems?: AltDocHubRow[]
}

function toExpirationFilterDays(
  value: string
): ReverificationExpirationFilterDays {
  const n = Number(value)
  return EXPIRATION_OPTIONS.find((option) => option === n) ?? 90
}

export const I9HubProvider = ({
  children,
  altDocsItems = [],
}: I9HubProviderProps) => {
  const [altQs, setAltQs] = useI9AltDocsHubQueryState()
  const [revQs, setRevQs] = useI9ReverificationHubQueryState()

  const expirationFilterDays = toExpirationFilterDays(revQs.revExpirationDays)

  const {
    data: reportRows = [],
    isPending: isRevLoading,
    isError: isRevError,
    error: revError,
    refetch,
    isFetching,
    dataUpdatedAt,
  } = useWorkAuthorizationExpirationReport()

  const { revMarketSelectOptions, revStateSelectOptions } = useMemo(
    () => ({
      revMarketSelectOptions: buildMarketSelectOptions(reportRows),
      revStateSelectOptions: buildStateSelectOptions(reportRows),
    }),
    [reportRows]
  )

  const clearRevState = useCallback(
    () => setRevQs({ revState: null }),
    [setRevQs]
  )
  const clearRevMarket = useCallback(
    () => setRevQs({ revMarket: null }),
    [setRevQs]
  )

  useClearStaleQueryParam(
    revQs.revState,
    revStateSelectOptions.map((option) => option.value),
    clearRevState
  )
  useClearStaleQueryParam(
    revQs.revMarket,
    revMarketSelectOptions.map((option) => option.value),
    clearRevMarket
  )

  const altMarketSelectOptions = useMemo(
    () => buildMarketSelectOptions(altDocsItems),
    [altDocsItems]
  )

  const altStateSelectOptions = useMemo(
    () => buildStateSelectOptions(altDocsItems),
    [altDocsItems]
  )

  const clearAltState = useCallback(
    () => setAltQs({ altState: null }),
    [setAltQs]
  )
  const clearAltMarket = useCallback(
    () => setAltQs({ altMarket: null }),
    [setAltQs]
  )

  useClearStaleQueryParam(
    altQs.altState,
    altStateSelectOptions.map((option) => option.value),
    clearAltState
  )
  useClearStaleQueryParam(
    altQs.altMarket,
    altMarketSelectOptions.map((option) => option.value),
    clearAltMarket
  )

  const altDocsFilteredItems = useMemo(
    () =>
      filterAltDocRows(altDocsItems, {
        selectedMarket: altQs.altMarket || null,
        selectedState: altQs.altState || null,
        workerId: altQs.altWorkerId ?? null,
        workerSearch: altQs.altWorkerSearch ?? '',
      }),
    [
      altDocsItems,
      altQs.altMarket,
      altQs.altState,
      altQs.altWorkerId,
      altQs.altWorkerSearch,
    ]
  )

  const altDocsGroupedItems = useMemo(
    () =>
      groupAltDocRows(
        altDocsFilteredItems,
        altQs.altGroupBy,
        altQs.altDateSort
      ),
    [altDocsFilteredItems, altQs.altGroupBy, altQs.altDateSort]
  )

  const reverificationFilteredRows = useMemo(
    () =>
      filterReverificationRows(reportRows, {
        expirationDays: expirationFilterDays,
        selectedMarket: revQs.revMarket || null,
        selectedState: revQs.revState || null,
        workerId: revQs.revWorkerId ?? null,
        workerSearch: revQs.revWorkerSearch ?? '',
      }),
    [
      reportRows,
      expirationFilterDays,
      revQs.revMarket,
      revQs.revState,
      revQs.revWorkerId,
      revQs.revWorkerSearch,
    ]
  )

  const reverificationGroupedRows = useMemo(
    () => groupReverificationRows(reverificationFilteredRows, revQs.revGroupBy),
    [reverificationFilteredRows, revQs.revGroupBy]
  )

  const value = useMemo<I9HubContextState>(
    () => ({
      state: {
        altDocs: {
          items: altDocsItems,
          filteredItems: altDocsFilteredItems,
          groupedItems: altDocsGroupedItems,
          marketSelectOptions: altMarketSelectOptions,
          stateSelectOptions: altStateSelectOptions,
          groupBy: altQs.altGroupBy,
          dateSort: altQs.altDateSort,
          filters: {
            state: altQs.altState ?? null,
            market: altQs.altMarket ?? null,
            workerId: altQs.altWorkerId ?? null,
            workerSearch: altQs.altWorkerSearch ?? '',
          },
        },
        reverification: {
          reportRows,
          filteredRows: reverificationFilteredRows,
          groupedRows: reverificationGroupedRows,
          marketSelectOptions: revMarketSelectOptions,
          stateSelectOptions: revStateSelectOptions,
          groupBy: revQs.revGroupBy,
          expirationFilterDays,
          filters: {
            state: revQs.revState ?? null,
            market: revQs.revMarket ?? null,
            workerId: revQs.revWorkerId ?? null,
            workerSearch: revQs.revWorkerSearch ?? '',
          },
          isLoading: isRevLoading,
          isError: isRevError,
          errorMessage: revError ? parseErrorMessage(revError) : '',
          isFetching,
          dataUpdatedAt,
        },
        counts: {
          alternateDocuments: altDocsFilteredItems.length,
          i9Reverification: reverificationFilteredRows.length,
        },
      },
      actions: {
        setAltDocsQuery: setAltQs,
        setReverificationQuery: setRevQs,
        refetchReverificationReport: refetch,
      },
    }),
    [
      altDocsItems,
      altDocsFilteredItems,
      altDocsGroupedItems,
      altMarketSelectOptions,
      altStateSelectOptions,
      altQs.altGroupBy,
      altQs.altDateSort,
      altQs.altState,
      altQs.altMarket,
      altQs.altWorkerId,
      altQs.altWorkerSearch,
      reportRows,
      reverificationFilteredRows,
      reverificationGroupedRows,
      revMarketSelectOptions,
      revStateSelectOptions,
      revQs.revGroupBy,
      revQs.revState,
      revQs.revMarket,
      revQs.revWorkerId,
      revQs.revWorkerSearch,
      expirationFilterDays,
      isRevLoading,
      isRevError,
      revError,
      isFetching,
      dataUpdatedAt,
      refetch,
      setAltQs,
      setRevQs,
    ]
  )

  if (isRevLoading) {
    return <Loading />
  }

  if (isRevError) {
    return (
      <Message variant="error" m={4}>
        Sorry, something went wrong loading the I-9 hub. Please reach out to eng
        support if this persists.
      </Message>
    )
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useI9HubContextValue }
